import { nativeShare } from '../native/platform';
/** Stable anonymous device id (anti-abuse signal for signup bonus/referrals; never contains personal data). */
export function deviceId(): string {
  const KEY = 'th.device';
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return 'no-storage-device';
  }
}

/**
 * RFC 4122 v4 id. `crypto.randomUUID` only exists on secure pages (https / localhost), so plain-http
 * LAN addresses such as http://192.168.x.x:5173 fall back to `crypto.getRandomValues`, which is
 * available everywhere.
 */
export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Copies text; works on insecure (plain-http) pages too, where navigator.clipboard is missing. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

export function vibrate(pattern: number | number[], enabled: boolean): void {
  if (enabled && 'vibrate' in navigator) navigator.vibrate(pattern);
}

export async function shareOrCopy(data: { title: string; text: string; url: string }): Promise<'shared' | 'copied' | 'failed'> {
  if (await nativeShare(data)) return 'shared';
  try {
    if (navigator.share) {
      await navigator.share(data);
      return 'shared';
    }
  } catch {
    /* user cancelled → fall back to copy */
  }
  return (await copyText(`${data.text}`)) ? 'copied' : 'failed';
}
